import { SVGIcon } from "../../components/SVGIcon/SVGIcon";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { customSanitize } from "@/utils/customSanitize.ts";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { ImageGridType } from "./ImageGrid";
import styles from "./ImageGridCard.module.scss";
import { ContentTag } from "@/components/ContentTag/ContentTag";
import { ButtonTypes } from "@/components/Button/Button";
import { MediaItemResponsive } from "@/components/MediaItem/MediaItemResponsive";
import { CustomLink } from "@/components/CustomLink";

interface ImageGridCardProps {
  element: Exclude<ImageGridType["image_grid"]["elements"], false>[number];
  gridType: ImageGridType["image_grid"]["grid_type"];
  showBoxShadow: boolean | undefined;
  showBorderRadius: boolean | undefined;
  showBox: boolean | undefined;
  mediaFormat: MediaFormatType;
  textAlignClass: string;
  showExtendedLink: boolean;
  type: "image" | "download";
  buttonType: ButtonTypes;
  heading_level?: number;
}

const ImageGridCard = ({
  showBoxShadow,
  showBorderRadius,
  element,
  mediaFormat,
  gridType,
  textAlignClass,
  buttonType,
  showBox,
  heading_level = 3,
}: ImageGridCardProps) => {
  const { getLabel, getIcon } = useGlobalContext();

  return (
    <article
      data-shadow={showBox && showBoxShadow}
      data-border-radius={showBox && showBorderRadius}
      className={`${styles["image-grid-element"]}`}
    >
      {element.link && (
        <CustomLink
          type={buttonType}
          href={element.link.url}
          target={element.link.target}
          aria-label={
            element.link.aria_label ??
            `${getLabel("read_more_label")}: ${element.title}`
          }
          disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
          className={styles["custom-link-overlay"]}
        >
          <span className={styles["only-sr"]}>{element.title}</span>
        </CustomLink>
      )}

      <div className={styles["element-content"]}>
        <div
          className={`${styles["image-wrapper"]} ${element.link ? styles["inverted-radius"] : ""} ${gridType === "title-in-image" ? styles["no-content"] : styles["has-content"]}`}
        >
          {element.image && (
            <>
              {element.image.mime_type === "image/svg+xml" ? (
                <SVGIcon src={element.image.url} />
              ) : (
                <MediaItemResponsive
                  hasOverlay={false}
                  image_desktop={element.image}
                  mediaFormat={mediaFormat}
                  sizes="(max-width: 768px) 100vw, (max-width: 1408px) 40vw, 640px"
                />
              )}

              {element.link && (
                <div className={styles["link-btn"]}>
                  <SVGIcon
                    src={getIcon("ArrowBigRight")}
                    width={14}
                    height={14}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <section
          className={`${styles["content-wrapper"]} ${textAlignClass} ${showBox ? "is-box" : ""}`}
        >
          <header>
            {element.title && (
              <span
                role="heading"
                aria-level={heading_level}
                className={`title ${styles["card-title"]} is-block`}
                dangerouslySetInnerHTML={{
                  __html: customSanitize(element.title),
                }}
              />
            )}

            {element.content && (
              <ContentTag
                extraClass={styles["card-content"]}
                content={element.content}
              />
            )}
          </header>

          {element.link && (
            <footer>
              <ContentTag
                extraClass={styles["link-text"]}
                content={element.link.title}
              />
            </footer>
          )}
        </section>
      </div>
    </article>
  );
};

export default ImageGridCard;
